// The admin Players page with memberships built in (MEMBERSHIPS_PLAN.md D47, D48).
// Pure helpers: the route query ⇄ the API's player filters, the tiles, the
// membership cell wording and the D47 setting's words. The API does the
// filtering and paging (never a browser-side join); this only shapes requests.

/** Route query keys on the Players page (names as D48 links use them). */
export const PLAYERS_QUERY = Object.freeze({
  keywords: 'q',
  membership: 'filters[membership]',
  statuses: 'filters[membershipStatuses]',
  inGroup: 'filters[inGroup]',
  sort: 'filters[sort]',
  page: 'page',
});

export const MEMBERSHIP_FILTER = Object.freeze({any: 'any', none: 'none'});

/** Status filter (OR'ed by the API). */
export const PLAYER_MEMBERSHIP_STATUS_OPTIONS = Object.freeze([
  {value: 'current', label: 'Current'},
  {value: 'paymentFailed', label: 'Payment failed'},
  {value: 'lapsed', label: 'Lapsed'},
  {value: 'ending', label: 'Ending'},
  {value: 'waiting', label: 'Waiting for approval or parent'},
]);
const STATUS_VALUES = PLAYER_MEMBERSHIP_STATUS_OPTIONS.map(o => o.value);

export const IN_GROUP_OPTIONS = Object.freeze([
  {value: 'true', label: 'In a group'},
  {value: 'false', label: 'Not in a group'},
]);

export const PLAYER_SORT_OPTIONS = Object.freeze([
  {value: '-joined', label: 'Newest first'},
  {value: 'joined', label: 'Oldest first'},
  {value: 'name', label: 'Name A to Z'},
  {value: '-name', label: 'Name Z to A'},
]);
export const DEFAULT_PLAYER_SORT = '-joined';
const SORT_VALUES = PLAYER_SORT_OPTIONS.map(o => o.value);

export const PLAYERS_PAGE_SIZE = 50;

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const one = (v) => (Array.isArray(v) ? v[0] : v);
const list = (v) => (Array.isArray(v) ? v : (v ? String(v).split(',') : [])).map(s => String(s).trim()).filter(Boolean);

/** The page's filter state from the route query (anything unknown is dropped). */
export function playersStateFromQuery(query = {}) {
  const membershipRaw = String(one(query[PLAYERS_QUERY.membership]) ?? '').trim();
  const membership = membershipRaw === MEMBERSHIP_FILTER.any || membershipRaw === MEMBERSHIP_FILTER.none || GUID.test(membershipRaw)
      ? membershipRaw.toLowerCase()
      : '';
  const inGroupRaw = String(one(query[PLAYERS_QUERY.inGroup]) ?? '');
  const sortRaw = String(one(query[PLAYERS_QUERY.sort]) ?? '');
  const page = Number.parseInt(String(one(query[PLAYERS_QUERY.page]) ?? '1'), 10);
  return {
    keywords: String(one(query[PLAYERS_QUERY.keywords]) ?? ''),
    membership,
    statuses: list(query[PLAYERS_QUERY.statuses]).filter(s => STATUS_VALUES.includes(s)),
    inGroup: inGroupRaw === 'true' ? true : inGroupRaw === 'false' ? false : null,
    sort: SORT_VALUES.includes(sortRaw) ? sortRaw : DEFAULT_PLAYER_SORT,
    page: Number.isFinite(page) && page > 0 ? page : 1,
  };
}

/**
 * The route query for a state, keeping every other key (dialogs, mailer). Empty
 * values and defaults are left out so a clean page has a clean address.
 */
export function playersQueryFromState(state, existing = {}) {
  const next = {...existing};
  const set = (key, value) => { if (value === undefined || value === null || value === '') delete next[key]; else next[key] = value; };
  set(PLAYERS_QUERY.keywords, state.keywords?.trim() || undefined);
  set(PLAYERS_QUERY.membership, state.membership || undefined);
  set(PLAYERS_QUERY.statuses, state.statuses?.length ? state.statuses.join(',') : undefined);
  set(PLAYERS_QUERY.inGroup, state.inGroup === true ? 'true' : state.inGroup === false ? 'false' : undefined);
  set(PLAYERS_QUERY.sort, state.sort && state.sort !== DEFAULT_PLAYER_SORT ? state.sort : undefined);
  set(PLAYERS_QUERY.page, state.page && state.page > 1 ? String(state.page) : undefined);
  return next;
}

/** Whether two states ask for the same rows (paging aside). */
export function sameFilters(a, b) {
  return (a.keywords ?? '').trim() === (b.keywords ?? '').trim()
      && (a.membership ?? '') === (b.membership ?? '')
      && (a.statuses ?? []).join(',') === (b.statuses ?? []).join(',')
      && (a.inGroup ?? null) === (b.inGroup ?? null)
      && (a.sort ?? DEFAULT_PLAYER_SORT) === (b.sort ?? DEFAULT_PLAYER_SORT);
}

/** The membership part of the filters, shared by the list, email, give-a-type and download (the API uses the same names). */
export function membershipFilterFields(state) {
  const f = {};
  const keywords = state.keywords?.trim();
  if (keywords) f.keywords = keywords;
  if (state.membership === MEMBERSHIP_FILTER.any || state.membership === MEMBERSHIP_FILTER.none) f.membership = state.membership;
  else if (state.membership) f.membershipTypeIds = [state.membership];
  if (state.statuses?.length) f.membershipStatuses = [...state.statuses];
  if (state.inGroup === true || state.inGroup === false) f.inGroup = state.inGroup;
  return f;
}

/** GET api/v2/players filters for one club's Players page. */
export function playersListFilters(clubId, state) {
  return {clubIds: [clubId], ...membershipFilterFields(state), sort: state.sort || DEFAULT_PLAYER_SORT};
}

/** Whether anything narrows the list (the bulk actions then say "these N players"). */
export function hasPlayerFilters(state) {
  return Object.keys(membershipFilterFields(state)).length > 0;
}

/** POST api/v2/users/mail filters: the ticked players, or the club plus the filters on screen. */
export function playersMailFilters(clubId, state, selectedIds = []) {
  if (selectedIds.length) return {playerIds: [...selectedIds]};
  return {clubIds: [clubId], ...membershipFilterFields(state)};
}

/** POST api/v2/clubPlayers/{clubId}/giveType body. */
export function giveTypeBody(state, {membershipTypeId, selectedIds = [], withoutMembershipOnly = true, settlement = 'NoCharge', startsOn = null} = {}) {
  const body = {membershipTypeId, withoutMembershipOnly: !!withoutMembershipOnly, settlement, startsOn: startsOn || null};
  if (selectedIds.length) return {...body, playerIds: [...selectedIds]};
  return {...body, ...membershipFilterFields(state)};
}

/** GET api/v2/clubPlayers/{clubId}/download data (format, answers, the filters on screen). */
export function playersDownloadData(state, {format = 'xlsx', answers = false} = {}) {
  return {format, answers: answers ? 'true' : 'false', filters: membershipFilterFields(state)};
}

/** "Lapsed · In a group · matching “jo”", for the download and email lines. */
export function playersScopeText(state, typeName = null) {
  const bits = [];
  if (state.membership === MEMBERSHIP_FILTER.any) bits.push('With a membership or pass');
  else if (state.membership === MEMBERSHIP_FILTER.none) bits.push('No membership or pass');
  else if (state.membership) bits.push(typeName || 'One type');
  const labels = PLAYER_MEMBERSHIP_STATUS_OPTIONS.filter(o => state.statuses?.includes(o.value)).map(o => o.label);
  if (labels.length) bits.push(labels.join(' or '));
  if (state.inGroup === true) bits.push('In a group');
  if (state.inGroup === false) bits.push('Not in a group');
  if (state.keywords?.trim()) bits.push(`matching “${state.keywords.trim()}”`);
  return bits.length ? bits.join(' · ') : 'Every player';
}

/** The tiles above the list (D48): what each counts and the filter it opens. */
export function playerTiles(summary) {
  if (!summary) return [];
  return [
    {key: 'players', label: 'Players', count: summary.players ?? 0, state: {}},
    {key: 'withMembership', label: 'With a membership or pass', count: summary.withMembership ?? 0, state: {membership: MEMBERSHIP_FILTER.any}},
    {key: 'paymentFailed', label: 'Payment failed', count: summary.paymentFailed ?? 0, state: {statuses: ['paymentFailed']}, warn: (summary.paymentFailed ?? 0) > 0},
    {key: 'applicationsWaiting', label: 'Applications waiting', count: summary.applicationsWaiting ?? 0, applications: true, warn: (summary.applicationsWaiting ?? 0) > 0},
  ];
}

/** A tile is "on" when the page shows exactly its filter. */
export function tileActive(tile, state) {
  if (tile.applications) return false;
  const want = {keywords: '', membership: '', statuses: [], inGroup: null, sort: state.sort, ...tile.state};
  return sameFilters(want, state);
}

/** The status line's chip colour (row summary statusKey). */
export function membershipStatusClass(statusKey) {
  switch (statusKey) {
    case 'current': return 'tl-chip--ok';
    case 'ending': return 'tl-chip--warn';
    case 'paymentFailed': return 'tl-chip--expired';
    case 'waiting':
    case 'waitingParent': return 'tl-chip--warn';
    default: return '';
  }
}

/** Icon for "Paid by". */
export function paidByIcon(kind) {
  switch (kind) {
    case 'self': return 'mdi-account-outline';
    case 'other': return 'mdi-account-arrow-right-outline';
    case 'guest': return 'mdi-account-question-outline';
    case 'outside': return 'mdi-cash';
    case 'free': return 'mdi-gift-outline';
    default: return null;
  }
}

/** Nothing was taken by card, so there is nothing to refund (TEST_T4 L9; the app hides Refund the same way). */
const NO_CARD_PAYMENT = ['outside', 'free'];

/**
 * The type chips a row shows: the current types, and a lapsed row's type that
 * lapsed (TEST_T4 M7), so the admin can see which one it was.
 */
export function summaryTypeChips(summary) {
  // The API sends the current types, then ones that lapsed or ended lately (X22); an older API sent current ones only.
  return summary?.types ?? [];
}

/** The chip colour for one type on a row, by that membership's own state. */
export function typeChipClass(chip) {
  switch (chip?.statusKey) {
    case 'paymentFailed':
    case 'waiting':
    case 'ending': return 'tl-chip--warn';
    case 'lapsed':
    case 'ended': return 'tl-chip--expired';
    case 'current': return 'tl-chip--ok';
    default: break;
  }
  switch (chip?.status) {
    case 'PastDue':
    case 'AwaitingPayment': return 'tl-chip--warn';
    case 'Lapsed':
    case 'Expired':
    case 'Cancelled': return 'tl-chip--expired';
    default: return 'tl-chip--ok';
  }
}

// Which memberships each money action can act on, by the membership's own status.
const ACTION_STATUSES = Object.freeze({
  changeType: ['Active', 'PastDue'],
  recordPayment: ['Active', 'PastDue', 'Lapsed', 'AwaitingPayment'],
  payLink: ['PastDue', 'Lapsed', 'AwaitingPayment'],
  cancel: ['Active', 'PastDue', 'AwaitingPayment'],
  refund: ['Active', 'PastDue'],
});
export const MONEY_ACTIONS = Object.freeze(Object.keys(ACTION_STATUSES));

const followsGroup = (chip) => chip?.isGroupFollower === true || (!!chip?.membershipGroupId && chip?.isGroupLead === false);

/**
 * The memberships a money action could mean on a row holding several (TEST_T4
 * several-per-player): one chip per membership, left out when it follows a
 * group (the lead has the money), isn't in a state the action fits, or (for a
 * refund) was never paid by card. Chips without those fields are kept: the
 * dialogs read the membership and the API has the final say.
 */
export function moneyActionChoices(summary, action) {
  const statuses = ACTION_STATUSES[action];
  if (!statuses) return [];
  const seen = new Set();
  return summaryTypeChips(summary).filter((chip) => {
    if (!chip?.membershipId || seen.has(chip.membershipId)) return false;
    seen.add(chip.membershipId);
    if (followsGroup(chip)) return false;
    if (chip.status && !statuses.includes(chip.status)) return false;
    if (action === 'refund' && chip.paidByKind && NO_CARD_PAYMENT.includes(chip.paidByKind)) return false;
    if (action === 'refund' && chip.canRefund === false) return false;
    return true;
  });
}

/** Whether a membership (the detail read) has a card payment a refund could return. */
export function hasCardPayment(membership) {
  if (!membership || membership.offlineSettled) return false;
  return (membership.periods ?? []).some(p => !!p.chargeId && ['Paid', 'Disputed'].includes(p.status));
}

/**
 * The membership actions a Players row's ⋮ offers, from the row's summary (so the
 * menu needs no extra read). Followers on a group carry no money of their own:
 * their rows offer the group and the lead's row has the money actions. With
 * several memberships, a money action is offered when any of them fits, and the
 * page asks which one (moneyActionChoices).
 * Keys: view, group, add, changeType, recordPayment, payLink, cancel, refund.
 */
export function membershipRowActionKeys(summary) {
  if (!summary) return [];
  const keys = [];
  const hasOne = !!summary.membershipId;
  if (hasOne) keys.push('view');
  if (summary.membershipGroupId) keys.push('group');
  keys.push('add');
  if (!hasOne) return keys;
  const money = new Set();
  if (!(summary.membershipGroupId && !summary.isGroupLead)) {
    const status = summary.statusKey;
    const current = status === 'current' || status === 'ending' || status === 'paymentFailed';
    const awaitingPayment = status === 'waiting' && !summary.applicationId;
    const owes = status === 'paymentFailed' || status === 'lapsed' || awaitingPayment;
    if (current) money.add('changeType');
    if (current || owes) money.add('recordPayment');
    if (owes) money.add('payLink');
    if (current || awaitingPayment) money.add('cancel');
    // The API says whether card money is left to give back (X22); before that, never for paid outside or free.
    const refundable = typeof summary.canRefund === 'boolean' ? summary.canRefund : !NO_CARD_PAYMENT.includes(summary.paidByKind);
    if (current && refundable) money.add('refund');
  }
  // Several memberships: the row's own one may not be the one with money on it.
  const chips = summaryTypeChips(summary);
  if (new Set(chips.map(c => c.membershipId)).size > 1) {
    for (const action of MONEY_ACTIONS) if (moneyActionChoices(summary, action).length) money.add(action);
  }
  return [...keys, ...MONEY_ACTIONS.filter(a => money.has(a))];
}

// ── D47 · Who can see the club's players ──

export const PLAYERS_VISIBLE_TO = Object.freeze({everyone: 'Everyone', holders: 'MembershipHolders'});

/** The grace period field: 0 to 365 whole days. */
export function graceDaysProblem(value) {
  if (value === '' || value === null || value === undefined) return 'Enter the number of days (0 for straight away)';
  const n = Number(value);
  if (!Number.isInteger(n) || n < 0 || n > 365) return 'The grace period is 0 to 365 days.';
  return null;
}

/** The confirm text before switching on (the count and the grace explanation). */
export function switchOnText(visibility, graceDays) {
  const n = visibility?.withoutMembership ?? 0;
  const who = n === 1 ? '1 player has' : `${n} players have`;
  if (n === 0) return 'Everyone has a membership or pass, so nobody loses the players list.';
  const days = Number(graceDays);
  const grace = days === 0
      ? "They lose the players list, club chats and name search in the club straight away."
      : `They get ${days} day${days === 1 ? '' : 's'} to choose one, with an email now and a reminder near the end, then lose the players list, club chats and name search in the club.`;
  return `${who} no membership or pass. ${grace} Competitions, bookings and the club page keep their own rules, and nobody leaves the club.`;
}

/** The member-side notice from clubPlayers/{clubId}/access or page/club. */
export function playersAccessNotice(access, page = null) {
  if (page?.playersLocked) return {state: 'locked', line: page.playersLockedLine || "Get a membership to see the club's players"};
  if (access && access.canSeePlayers === false) return {state: 'locked', line: access.line || "Get a membership to see the club's players"};
  if (access?.inGrace && access.line) return {state: 'grace', line: access.line, graceEndsAt: access.graceEndsAt ?? null};
  return {state: 'open', line: ''};
}

// ── Player detail: memberships, periods and payments ──

const PERIOD_STATUS_TEXT = Object.freeze({
  Scheduled: 'Not due yet', Charging: 'Taking payment', Paid: 'Paid', Failed: 'Payment failed', Refunded: 'Refunded',
  Waived: 'No charge', SettledOffline: 'Paid outside Tap Leagues', Authorised: 'Card held',
});
/** A membership period's payment state in words. */
export function periodStatusText(status) {
  return PERIOD_STATUS_TEXT[status] ?? (status ? String(status) : '');
}

/** The amount a period carried: what was recorded outside, else the price plus any joining fee. */
export function periodAmountMinor(period) {
  if (!period) return 0;
  if (period.status === 'SettledOffline' && period.offlineAmountMinor != null) return period.offlineAmountMinor;
  return (period.amountMinor ?? 0) + (period.joiningFeeMinor ?? 0);
}

/** Current ones first, then the most recent start. */
export function sortPlayerMemberships(memberships = []) {
  return [...memberships].sort((a, b) => {
    if (!!a.isCurrent !== !!b.isCurrent) return a.isCurrent ? -1 : 1;
    return String(b.startedAt ?? '').localeCompare(String(a.startedAt ?? ''));
  });
}

// ── "Who can see your club" (PRIVACY_PLAN.md §2b + D47, X32) ──
// One select in Club settings with three tiers. The third saves the club as Club players
// only (Visibility 1) and then the D47 players setting as People with a membership or pass;
// the first two save the players setting as Everyone in the club.
export const CLUB_TIER = Object.freeze({public: 0, clubPlayers: 1, holders: 2});

export const CLUB_VISIBILITY_OPTIONS = Object.freeze([
  {value: CLUB_TIER.public, title: 'Public', hint: 'Anyone can see your players, competitions, standings and results. People outside the club see names as first name and initial.'},
  {value: CLUB_TIER.clubPlayers, title: 'Club players only', hint: 'Club players see the player list, club chat, competitions, standings and results. Everyone else sees the club name, logo, player count and address, and a Join button if your invite link is shown to everyone.'},
  {value: CLUB_TIER.holders, title: 'Club players only, player list needs a membership', hint: 'Club players see competitions, standings and results, and can enter anything open to them. Only people with a membership or pass see the player list, club chat and name search. Everyone else sees the club name, logo, player count and address, and a Join button if your invite link is shown to everyone.'},
]);

/**
 * The tier a club is on (X31's ClubVisibilityRules.Tier): a Public club is Public whatever the
 * players setting says (the API resets it to Everyone when a club is saved as Public); a club
 * players only club with MembershipHolders is the third tier.
 */
export function clubVisibilityTier(visibility, playersVisibleTo) {
  if ((visibility ?? 1) === 0 || visibility === 'Public') return CLUB_TIER.public;
  return playersVisibleTo === PLAYERS_VISIBLE_TO.holders ? CLUB_TIER.holders : CLUB_TIER.clubPlayers;
}

/** What a tier saves: the club's Visibility and the D47 players setting. */
export function clubTierSaves(tier) {
  return tier === CLUB_TIER.holders
      ? {visibility: 1, playersVisibleTo: PLAYERS_VISIBLE_TO.holders}
      : {visibility: tier === CLUB_TIER.public ? 0 : 1, playersVisibleTo: PLAYERS_VISIBLE_TO.everyone};
}

/**
 * Whether the third tier is offered: the API's canRestrictPlayersToMembers on the club (X31: memberships
 * on + a live type, sent to admins) or, on an older API, the D47 response's canChoose (X31 makes that
 * false on a Public club, so it is only the fallback); always while it is on, so the select can show it.
 */
export function canOfferHoldersTier(club, d47) {
  if (clubVisibilityTier(club?.visibility, club?.playersVisibleTo ?? d47?.playersVisibleTo) === CLUB_TIER.holders) return true;
  if (typeof club?.canRestrictPlayersToMembers === 'boolean') return club.canRestrictPlayersToMembers;
  return !!(club?.membershipsEnabled && d47?.canChoose);
}

/** The options for the select (the third only when offered). */
export function clubVisibilityOptions(offerHolders) {
  return CLUB_VISIBILITY_OPTIONS.filter(o => offerHolders || o.value !== CLUB_TIER.holders);
}

/**
 * X100: the club PUT the Memberships & passes Settings card sends when its "Who can see your club"
 * choice changes the club's Visibility. The same endpoint and body as Club settings (the whole
 * club as loaded), with only Visibility taken from the card's draft, so no other club field moves.
 */
export function clubVisibilitySaveBody(club, draft) {
  return {...(club ?? {}), visibility: draft?.visibility ?? club?.visibility};
}

/** X100: whether the card's draft moves the club's Visibility (the D47 part saves on its own). */
export function clubVisibilityChanged(club, draft) {
  return !!club && !!draft && (draft.visibility ?? 1) !== (club.visibility ?? 1);
}
